import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { CtyButton } from '../button/CtyButton';
import { cn } from './cn';

export type PromptRadialAction = {
  id: string;
  icon: string;
  label: string;
  iconClassName?: string;
  onPress: () => void;
  /** Show a spinner ring while an async action is in progress. */
  isLoading?: boolean;
  /** Show a connected/active pulse ring. */
  isActive?: boolean;
  /** Default true. Set false to keep the menu open (e.g. voice agent connecting). */
  closeMenuOnPress?: boolean;
  /** Defer closing so native pickers (file dialog) can open first. */
  deferCloseMenu?: boolean;
};

type PromptActionsRadialMenuProps = {
  actions: PromptRadialAction[];
  isDisabled?: boolean;
  className?: string;
  /** Show a loading ring on the + hub (e.g. voice agent API pending). */
  hubLoading?: boolean;
};

/** Minimum arc length a segment needs at the icon radius. */
const ITEM_MIN_SPACING_PX = 52;
const MIN_RADIUS_PX = 84;
/** Radial thickness of the segment ring. */
const RING_THICKNESS_PX = 56;
const ITEM_HALF_SIZE_PX = 22;
const VIEWPORT_PADDING_PX = 12;
/** Breathing room around the wheel so strokes and shadows are not clipped. */
const SVG_PADDING_PX = 10;
/** Gap between the top of the wheel and the hovered action tooltip. */
const TOOLTIP_GAP_PX = 10;
/** Space kept above the wheel so the tooltip stays inside the viewport. */
const TOOLTIP_RESERVE_PX = 40;
/** Half wheel around the hub: left (-180°) through top (-90°) to right (0°). */
export const FAN_START_DEG = -180;
export const FAN_END_DEG = 0;

const TRIGGER_SLIDE_MS = 280;
const FAN_REVEAL_DELAY_MS = 90;
const FAN_STAGGER_MS = 45;
const FAN_ITEM_ANIM_MS = 420;
const BACKDROP_DELAY_MS = TRIGGER_SLIDE_MS + 80;
const FAN_BOUNCE_EASE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/** Outer radius of the wheel, sized so every segment can hold an icon. */
export function getRadialRadiusForCount(total: number): number {
  if (total <= 0) return MIN_RADIUS_PX;
  const segmentRad = ((FAN_END_DEG - FAN_START_DEG) * Math.PI) / 180 / total;
  const requiredIconRadius = ITEM_MIN_SPACING_PX / segmentRad;
  return Math.max(MIN_RADIUS_PX, Math.ceil(requiredIconRadius + RING_THICKNESS_PX / 2));
}

/** Inner radius (the hub disc) for a given outer radius. */
export function getInnerRadius(outerRadius: number): number {
  return Math.max(outerRadius * 0.4, outerRadius - RING_THICKNESS_PX);
}

export function getSegmentAngles(index: number, total: number) {
  const spanDeg = (FAN_END_DEG - FAN_START_DEG) / Math.max(total, 1);
  const startDeg = FAN_START_DEG + spanDeg * index;
  return { startDeg, endDeg: startDeg + spanDeg, midDeg: startDeg + spanDeg / 2 };
}

/** Position of a segment's icon, relative to the hub centre. */
export function getRadialOffset(index: number, total: number, radius: number) {
  const { midDeg } = getSegmentAngles(index, total);
  const angleRad = (midDeg * Math.PI) / 180;
  return {
    x: radius * Math.cos(angleRad),
    y: radius * Math.sin(angleRad),
  };
}

/** Clamp radius so the shifted half wheel (-180°..0°) stays inside the viewport. */
export function clampRadiusToViewport(
  radius: number,
  closedCenterX: number,
  closedCenterY: number,
): number {
  const leftLimit = (closedCenterX - VIEWPORT_PADDING_PX - ITEM_HALF_SIZE_PX) / 2;
  const topLimit = closedCenterY - VIEWPORT_PADDING_PX - TOOLTIP_RESERVE_PX;
  return Math.max(MIN_RADIUS_PX, Math.floor(Math.min(radius, leftLimit, topLimit)));
}

function polarPoint(cx: number, cy: number, radius: number, angleDeg: number) {
  const angleRad = (angleDeg * Math.PI) / 180;
  return { x: cx + radius * Math.cos(angleRad), y: cy + radius * Math.sin(angleRad) };
}

/** Path for a ring slice between two radii and two angles. */
export function describeAnnularSector(
  cx: number,
  cy: number,
  innerRadius: number,
  outerRadius: number,
  startDeg: number,
  endDeg: number,
): string {
  const largeArc = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  const outerStart = polarPoint(cx, cy, outerRadius, startDeg);
  const outerEnd = polarPoint(cx, cy, outerRadius, endDeg);
  const innerEnd = polarPoint(cx, cy, innerRadius, endDeg);
  const innerStart = polarPoint(cx, cy, innerRadius, startDeg);

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
}

/** Upper half arc, left to right, without the closing chord. */
function describeHalfArc(cx: number, cy: number, radius: number): string {
  return `M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`;
}

function LoadingRing({ className }: Readonly<{ className?: string }>) {
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 z-0 text-primary motion-reduce:animate-none animate-[spin_0.75s_linear_infinite]',
        className,
      )}
    >
      <svg className="h-full w-full" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="20" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
        <circle
          cx="24"
          cy="24"
          r="20"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="32 94"
          transform="rotate(-90 24 24)"
        />
      </svg>
    </div>
  );
}

function RadialSegmentIcon({
  action,
  left,
  top,
  opacity,
  transition,
  isHighlighted,
}: Readonly<{
  action: PromptRadialAction;
  left: number;
  top: number;
  opacity: number;
  transition: string;
  isHighlighted: boolean;
}>) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute motion-reduce:transition-none"
      style={{ left, top, opacity, transition, transform: 'translate(-50%, -50%)' }}
    >
      <div className="relative flex h-9 w-9 items-center justify-center overflow-visible">
        {action.isLoading && <LoadingRing />}
        {action.isActive && (
          <>
            <span className="pointer-events-none absolute inset-0 scale-90 rounded-full border-2 border-success shadow-[0_0_12px_rgba(34,197,94,0.45)] motion-reduce:animate-none motion-reduce:opacity-40 animate-ping" />
            <span className="pointer-events-none absolute inset-0 scale-90 rounded-full border-2 border-success/70 motion-reduce:animate-none motion-reduce:opacity-30 animate-ping [animation-delay:0.65s]" />
          </>
        )}
        <Icon
          icon={action.icon}
          width={20}
          height={20}
          className={cn(
            'relative z-[2] transition-transform duration-150 motion-reduce:transition-none',
            isHighlighted && 'scale-110',
            action.iconClassName ?? 'text-primary',
          )}
        />
      </div>
    </div>
  );
}

function RadialSegment({
  action,
  path,
  isHighlighted,
  isInteractive,
  opacity,
  transition,
  onHighlight,
  onSelect,
}: Readonly<{
  action: PromptRadialAction;
  path: string;
  isHighlighted: boolean;
  isInteractive: boolean;
  opacity: number;
  transition: string;
  onHighlight: (id: string | null) => void;
  onSelect: (action: PromptRadialAction) => void;
}>) {
  return (
    <g
      role="menuitem"
      tabIndex={0}
      aria-label={action.label}
      aria-busy={action.isLoading}
      className={cn(
        'cursor-pointer outline-none transition-colors duration-150 motion-reduce:transition-none',
        isHighlighted ? 'text-primary/25' : 'text-content1',
      )}
      style={{ opacity, transition }}
      onMouseEnter={() => onHighlight(action.id)}
      onMouseLeave={() => onHighlight(null)}
      onFocus={() => onHighlight(action.id)}
      onBlur={() => onHighlight(null)}
      onClick={() => onSelect(action)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onSelect(action);
      }}
    >
      <path d={path} fill="currentColor" style={{ pointerEvents: isInteractive ? 'all' : 'none' }} />
    </g>
  );
}

/** Divider lines plus the inner and outer rims that make the slices readable. */
function WheelRims({
  total,
  hubX,
  hubY,
  innerRadius,
  outerRadius,
}: Readonly<{
  total: number;
  hubX: number;
  hubY: number;
  innerRadius: number;
  outerRadius: number;
}>) {
  return (
    <g fill="none" stroke="currentColor" className="pointer-events-none text-divider">
      <path d={describeHalfArc(hubX, hubY, outerRadius)} />
      <path d={describeHalfArc(hubX, hubY, innerRadius)} />
      <line x1={hubX - outerRadius} y1={hubY} x2={hubX - innerRadius} y2={hubY} />
      <line x1={hubX + innerRadius} y1={hubY} x2={hubX + outerRadius} y2={hubY} />
      {Array.from({ length: Math.max(total - 1, 0) }, (_, index) => {
        const { startDeg } = getSegmentAngles(index + 1, total);
        const inner = polarPoint(hubX, hubY, innerRadius, startDeg);
        const outer = polarPoint(hubX, hubY, outerRadius, startDeg);
        return <line key={startDeg} x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y} />;
      })}
    </g>
  );
}

function RadialHalfWheel({
  actions,
  outerRadius,
  itemsVisible,
  onSelect,
}: Readonly<{
  actions: PromptRadialAction[];
  outerRadius: number;
  itemsVisible: boolean;
  onSelect: (action: PromptRadialAction) => void;
}>) {
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const innerRadius = getInnerRadius(outerRadius);
  const iconRadius = (outerRadius + innerRadius) / 2;
  const width = 2 * (outerRadius + SVG_PADDING_PX);
  const height = outerRadius + 2 * SVG_PADDING_PX;
  const hubX = width / 2;
  const hubY = height - SVG_PADDING_PX;
  const highlightedLabel = actions.find((action) => action.id === highlightedId)?.label ?? '';

  return (
    <div
      className="pointer-events-none absolute motion-reduce:transition-none"
      style={{
        width,
        height,
        left: -hubX,
        top: -hubY,
        transformOrigin: `${hubX}px ${hubY}px`,
        transform: itemsVisible ? 'scale(1)' : 'scale(0.25)',
        opacity: itemsVisible ? 1 : 0,
        transition: itemsVisible
          ? `transform ${FAN_ITEM_ANIM_MS}ms ${FAN_BOUNCE_EASE}, opacity 220ms ease-out`
          : 'transform 160ms ease-in, opacity 120ms ease-in',
      }}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="pointer-events-none overflow-visible drop-shadow-lg"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d={describeAnnularSector(hubX, hubY, innerRadius, outerRadius, FAN_START_DEG, FAN_END_DEG)}
          fill="currentColor"
          className="text-content1"
        />
        {actions.map((action, index) => {
          const { startDeg, endDeg } = getSegmentAngles(index, actions.length);
          return (
            <RadialSegment
              key={action.id}
              action={action}
              path={describeAnnularSector(hubX, hubY, innerRadius, outerRadius, startDeg, endDeg)}
              isHighlighted={action.id === highlightedId}
              isInteractive={itemsVisible}
              opacity={itemsVisible ? 1 : 0}
              transition={`opacity 200ms ease-out ${index * FAN_STAGGER_MS}ms`}
              onHighlight={setHighlightedId}
              onSelect={onSelect}
            />
          );
        })}
        <WheelRims
          total={actions.length}
          hubX={hubX}
          hubY={hubY}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
        />
      </svg>

      {actions.map((action, index) => {
        const { x, y } = getRadialOffset(index, actions.length, iconRadius);
        return (
          <RadialSegmentIcon
            key={action.id}
            action={action}
            left={hubX + x}
            top={hubY + y}
            opacity={itemsVisible ? 1 : 0}
            transition={`opacity 220ms ease-out ${index * FAN_STAGGER_MS}ms`}
            isHighlighted={action.id === highlightedId}
          />
        );
      })}

      <div
        aria-live="polite"
        className="pointer-events-none absolute flex justify-center transition-opacity duration-150 motion-reduce:transition-none"
        style={{
          left: hubX,
          top: hubY - outerRadius - TOOLTIP_GAP_PX,
          maxWidth: outerRadius * 1.8,
          transform: 'translate(-50%, -100%)',
          opacity: highlightedLabel ? 1 : 0,
        }}
      >
        <span className="truncate rounded-medium border border-divider bg-content1 px-2 py-1 text-xs font-medium text-foreground shadow-md">
          {highlightedLabel}
        </span>
      </div>
    </div>
  );
}

export default function PromptActionsRadialMenu({
  actions,
  isDisabled = false,
  className,
  hubLoading = false,
}: Readonly<PromptActionsRadialMenuProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [itemsVisible, setItemsVisible] = useState(false);
  const [showBackdrop, setShowBackdrop] = useState(false);
  const [closedCenter, setClosedCenter] = useState<{ x: number; y: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const fanHubRef = useRef<HTMLDivElement>(null);

  const idealRadius = useMemo(() => getRadialRadiusForCount(actions.length), [actions.length]);

  const radius = useMemo(() => {
    if (!closedCenter) return idealRadius;
    return clampRadiusToViewport(idealRadius, closedCenter.x, closedCenter.y);
  }, [closedCenter, idealRadius]);

  const captureClosedCenter = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return null;
    const rect = trigger.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }, []);

  const syncFanHubPosition = useCallback(() => {
    const trigger = triggerRef.current;
    const fanHub = fanHubRef.current;
    if (!trigger || !fanHub) return;
    const rect = trigger.getBoundingClientRect();
    fanHub.style.left = `${rect.left + rect.width / 2}px`;
    fanHub.style.top = `${rect.top + rect.height / 2}px`;
  }, []);

  const closeMenu = useCallback(() => {
    setShowBackdrop(false);
    setItemsVisible(false);
    window.setTimeout(() => {
      setIsOpen(false);
      setClosedCenter(null);
    }, 160);
  }, []);

  const openMenu = useCallback(() => {
    const center = captureClosedCenter();
    if (center) setClosedCenter(center);
    setIsOpen(true);
  }, [captureClosedCenter]);

  const isAnyActionLoading = useMemo(
    () => actions.some((action) => action.isLoading),
    [actions],
  );
  const keepMenuOpenForLoading = hubLoading || isAnyActionLoading;

  const handleToggle = useCallback(() => {
    if (isOpen) {
      if (keepMenuOpenForLoading) return;
      closeMenu();
    } else {
      openMenu();
    }
  }, [closeMenu, isOpen, keepMenuOpenForLoading, openMenu]);

  const handleActionPress = useCallback((action: PromptRadialAction) => {
    action.onPress();

    if (action.closeMenuOnPress === false) return;

    const finishClose = () => {
      (document.activeElement as HTMLElement | null)?.blur();
      closeMenu();
    };

    if (action.deferCloseMenu) {
      window.setTimeout(finishClose, 0);
      return;
    }

    finishClose();
  }, [closeMenu]);

  useEffect(() => {
    if (!keepMenuOpenForLoading || isOpen) return;
    openMenu();
  }, [keepMenuOpenForLoading, isOpen, openMenu]);

  useEffect(() => {
    if (!isOpen) {
      setItemsVisible(false);
      setShowBackdrop(false);
      return;
    }

    if (keepMenuOpenForLoading) {
      setItemsVisible(true);
      return;
    }

    const revealTimer = window.setTimeout(() => setItemsVisible(true), FAN_REVEAL_DELAY_MS);
    const backdropTimer = window.setTimeout(() => setShowBackdrop(true), BACKDROP_DELAY_MS);

    return () => {
      window.clearTimeout(revealTimer);
      window.clearTimeout(backdropTimer);
    };
  }, [isOpen, keepMenuOpenForLoading]);

  useEffect(() => {
    if (!isOpen) return;

    syncFanHubPosition();
    let rafId = 0;
    const tick = () => {
      syncFanHubPosition();
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);

    const viewport = window.visualViewport;
    window.addEventListener('resize', syncFanHubPosition);
    window.addEventListener('scroll', syncFanHubPosition, true);
    viewport?.addEventListener('resize', syncFanHubPosition);
    viewport?.addEventListener('scroll', syncFanHubPosition);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', syncFanHubPosition);
      window.removeEventListener('scroll', syncFanHubPosition, true);
      viewport?.removeEventListener('resize', syncFanHubPosition);
      viewport?.removeEventListener('scroll', syncFanHubPosition);
    };
  }, [isOpen, radius, syncFanHubPosition]);

  if (actions.length === 0) return null;

  let triggerTooltip = 'More actions';
  if (hubLoading) triggerTooltip = 'Connecting…';
  else if (isOpen) triggerTooltip = 'Close';

  const fanLayer = isOpen && typeof document !== 'undefined'
    ? createPortal(
        <>
          {showBackdrop && !keepMenuOpenForLoading && (
            <button
              type="button"
              aria-label="Close actions menu"
              className="fixed inset-0 z-[9998] cursor-default bg-transparent"
              onPointerDown={(event) => {
                event.preventDefault();
                closeMenu();
              }}
            />
          )}
          <div
            ref={fanHubRef}
            role="menu"
            aria-label="Prompt actions"
            className="fixed z-[9999] h-0 w-0"
          >
            <RadialHalfWheel
              actions={actions}
              outerRadius={radius}
              itemsVisible={itemsVisible}
              onSelect={handleActionPress}
            />
          </div>
        </>,
        document.body,
      )
    : null;

  return (
    <div
      ref={triggerRef}
      className={cn('relative shrink-0 overflow-visible motion-reduce:transition-none', className)}
      data-testid="prompt-actions-radial-menu"
      style={{
        transform: isOpen ? `translateX(-${radius}px)` : 'translateX(0)',
        transition: `transform ${TRIGGER_SLIDE_MS}ms ${FAN_BOUNCE_EASE}`,
        zIndex: isOpen ? 10000 : undefined,
      }}
    >
      {fanLayer}

      <div className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-visible">
        {hubLoading && <LoadingRing className="-inset-0.5" />}
        <CtyButton
          id="prompt-radial-menu-trigger"
          isIconOnly
          radius="full"
          size="sm"
          variant="light"
          isDisabled={isDisabled}
          tooltipContent={triggerTooltip}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-busy={hubLoading}
          className={cn(
            'relative z-[2]',
            isOpen && 'border border-divider bg-content1 shadow-lg',
          )}
          onPress={handleToggle}
        >
          <Icon
            icon={isOpen ? 'mdi:close' : 'mdi:plus'}
            width={isOpen ? 22 : 20}
            height={isOpen ? 22 : 20}
            className={cn(
              'text-default-600 motion-reduce:transition-none transition-transform duration-300',
              isOpen && 'rotate-90 text-foreground',
            )}
          />
        </CtyButton>
      </div>
    </div>
  );
}
